import { environment } from '@1bill-app/env';
import { BillService } from '@1bill-app/services/bill/bill.service';
import {
  PaymentCard,
  PaymentCardService,
} from '@1bill-app/services/payment/payment-card.service';
import { SettingsQuery } from '@1bill-app/services/settings/settings.query';
import { SettingsService } from '@1bill-app/services/settings/settings.service';
import { Component, OnInit, ViewChild } from '@angular/core';
import { AlertController, LoadingController, ToastController } from '@ionic/angular';
import { NGXLogger } from 'ngx-logger';
import { SubSink } from 'subsink';
import { showError } from 'src/app/services/custom-functions/custom-functions';
import { BillQuery } from '@1bill-app/services/bill/bill.query';
import { GetBillOption, ViewBillType } from '@1bill-app/services/bill/types';
import { BillStore } from '@1bill-app/services/bill/bill.store';
import { PaymentOptionsSliderComponent } from '@1bill-app/components/payment-options-slider/payment-options-slider.component';
import { LinkedBillsListComponent } from '@1bill-app/components/linked-bills-list/linked-bills-list.component';
import { StorageItemKeys, StorageService } from '@1bill-app/services/storage.service';
import { ActivatedRoute, Router } from '@angular/router';
import { YodleeQuery } from '@1bill-app/services/yodlee/yodlee.query';
import { YodleeService } from '@1bill-app/services/yodlee/yodlee.service';
import { YodleeAccount } from '@1bill-app/services/yodlee/yodlee.types';
import { first } from 'lodash';
import { Observable } from 'rxjs';
import { map, mergeMap, tap } from 'rxjs/operators';
import { ROUTE_USER_PROFILE } from '@1bill-app/constants';

@Component({
  selector: 'app-payment-options',
  templateUrl: './payment-options.page.html',
  styleUrls: ['./payment-options.page.scss'],
})
export class PaymentOptionsPage implements OnInit {
  constructor(
    private settingsQuery: SettingsQuery,
    private logger: NGXLogger,
    private billService: BillService,
    private billQuery: BillQuery,
    private billStore: BillStore,
    private settingsService: SettingsService,
    private toastController: ToastController,
    private loadingController: LoadingController,
    private alertController: AlertController,
    private route: ActivatedRoute,
    private router: Router,
    private paymentCardService: PaymentCardService,
    private storageService: StorageService,
    private yodleeQuery: YodleeQuery,
    private yodleeService: YodleeService,
  ) {}

  @ViewChild('slider') slider!: PaymentOptionsSliderComponent;
  @ViewChild('linkedBillsList') linkedBillsList: LinkedBillsListComponent;

  paymentOptionIndex = 0;

  subs = new SubSink();
  defaultHref = ROUTE_USER_PROFILE;

  accountCards$ = this.settingsQuery.accountCards$;
  accountCards: PaymentCard[];
  defaultAccountCardId: number;

  //paymentInstForm: FormGroup;
  //isFormUntouched: boolean = true;
  saving: boolean;
  newNickname: string = '';
  initRun = true;

  accounts: YodleeAccount[];

  public accounts$: Observable<YodleeAccount[]> = this.route.queryParams.pipe(
    mergeMap((param) =>
      this.yodleeQuery.accounts$.pipe(
        map((accounts) => {
          // const type = "cash";
          // if (!type) {
          //   //this.router.navigateByUrl(ROUTE_HOMEPAGE);
          //   return;
          // }
          //this.type = type;
          //this.title = this.type.charAt(0).toUpperCase() + this.type.slice(1);
          this.accounts = accounts?.filter((account) => account.isManualAccount === false);
          return accounts?.filter((account) => account.isManualAccount === false);
        }),
        tap((accounts) => {
          const account = first(accounts);
          //this.groupTitle = account?.displayGroupType;
        }),
      ),
    ),
  );

  /**
   * All of the user's unpaid bills.
   */
  _bills: ViewBillType[];
  get bills() {
    return this._bills;
  }

  /**
   * All of the user's unpaid bills. Setting will update `paymentOptionsBills` property in `BillStore`.
   */
  set bills(newBills: ViewBillType[]) {
    this._bills = newBills;
    this.billStore.update({ paymentOptionsBills: newBills });
  }

  /**
   * List of bills currently linked by the selected payment method.
   */
  linkedBills: ViewBillType[];

  /**
   * Updating the selected payment method ID updates the array of linked bills.
   */
  selectedAccountPaymentId: number;

  ngOnInit() {
    this.subs.sink = this.yodleeService.getAccounts().subscribe();

    this.settingsQuery
      .select(['accountCards', 'defaultAccountCardId'])
      .subscribe((accountSettings) => {
        this.accountCards = accountSettings.accountCards.sort((a) =>
          a.accountPaymentId === accountSettings.defaultAccountCardId ? -1 : 1,
        );
        if (
          accountSettings.defaultAccountCardId &&
          accountSettings.accountCards?.length &&
          this.initRun
        ) {
          this.selectedAccountPaymentId = this.accountCards[0]?.accountPaymentId;
          this.newNickname = this.accountCards.find(
            (accountCard) =>
              accountCard.accountPaymentId === accountSettings.defaultAccountCardId,
          )?.nickname;
          this.initRun = false;
          this.updateLinkedBills();
        }
      });

    this.settingsQuery.defaultAccountCardId$.subscribe({
      next: (defaultAccountCardId) => {
        this.defaultAccountCardId = defaultAccountCardId;
      },
      error: (err) => {
        this.logger.error(
          'Unable to retrieve user default account card ID from data store:',
          err,
        );
      },
    });

    // this.bills cannot be directly overwritten, as this would cause issues where the swap
    // bill component modal no longer links to the original element (as it is flushed)
    this.fetchData(!this.billQuery.getValue().billPayments?.length);
    this.billQuery.getBillsForView(GetBillOption.UNPAID_BILLS).subscribe((bills) => {
      if (
        !this.bills?.length ||
        !this.bills.every(
          (bill, index) => bills[index].linkedPaymentMethodId === bill.linkedPaymentMethodId,
        )
      ) {
        this.bills = bills;
      }
      this.updateLinkedBills();
    });

    //Needed for adding new cards
    this.route.queryParams.subscribe((p) => {
      this.logger.log('payment listener received params:', p);
      if (p?.CallbackStatus) {
        this.paymentTokenListener(p);
      }
    });
  }

  // Needed for adding a new card
  async paymentTokenListener(p: any) {
    if (p.CallbackStatus.toLocaleLowerCase() !== 'successful') {
      this.router.navigate(['.'], { relativeTo: this.route });
      await showError('Something went wrong. Card Not Added', true);
      return;
    }

    if (p.Token && p.CallbackStatus && p.CallbackStatus.toLocaleLowerCase() === 'successful') {
      const loader = await this.loadingController.create({
        message: 'Updating card details...',
      });
      await loader.present();
      try {
        const replacingCardId = await this.storageService.getItem(
          StorageItemKeys.REPLACE_CARD_ID,
        );
        let response;
        if (isNaN(Number(replacingCardId)) || replacingCardId === null) {
          response = await this.paymentCardService.save(p).toPromise();
        } else {
          response = await this.paymentCardService
            .replacePaymentCard(p, Number(replacingCardId))
            .toPromise()
            .finally(async () => {
              await this.storageService.removeItem(StorageItemKeys.REPLACE_CARD_ID);
            });
        }
        if (response?.success === false) {
          await showError('Card details already added.', true);
        }
        await this.fetchData();
        await loader.dismiss();
      } catch (err) {
        await this.fetchData();
        await loader.dismiss();
      }
      this.router.navigate(['.'], { relativeTo: this.route });
    }
  }

  trackByFn(index: number, item: PaymentCard) {
    return item.accountPaymentId;
  }

  getCardBackground(i: any) {
    if (this.accountCards[i].cardType === 'MasterCard') {
      return './assets/cards/MasterCard.png';
    } else if (this.accountCards[i].cardType === 'Visa') {
      return './assets/cards/Visa.png';
    } else if (this.accountCards[i].cardType === 'American Express') {
      return './assets/cards/American Express.png';
    }
  }

  getCardKind(i: any) {
    switch (this.accountCards[i].cardType) {
      case 'MasterCard':
        return 'DEBIT';
      case 'Visa':
        return 'CREDIT';
      case 'American Express':
        return 'CREDIT';
    }
  }

  currentPaymentOption() {
    return this.accountCards[this.paymentOptionIndex];
  }

  /**
   * If refreshing on this page, then `billQuery.getBillsForView()` in `ngOnInit` will complete before the subscription.
   * If navigating to this page from the dashboard, the subscription will complete immediately (since the account settings
   * data is already set beforehand), and `bills` as the subscription completes.
   * The set handler of `selectedAccountPaymentId` must be run after `bills` is set, as the linked bills list will update
   * according to the value of `selectedAccountPaymentId`.
   */
  private async updateLinkedBills() {
    this.linkedBills = this.bills?.filter(
      (bill) => bill.linkedPaymentMethodId === this.selectedAccountPaymentId,
    );
    setTimeout(() => {
      this.linkedBillsList?.updateMoreBills();
    }, 0);
  }

  /**
   * If the user has no payment methods (cards, [bank accounts in the future]) linked to their account, returns true.
   * Else, returns false.
   * Used to determine whether or not certain components on the page should display.
   */
  noAccountCards() {
    return this.accountCards?.length === 0;
  }

  isLastSelected() {
    return this.paymentOptionIndex === this.accountCards?.length;
  }

  /**
   * If PaymentCard has a nickname, return nickname.
   * Otherwise, return cardTitle
   */
  getCardTitle() {
    if (this.paymentOptionIndex >= this.accountCards.length)
      if (Boolean(this.accountCards[this.paymentOptionIndex].nickname))
        return this.accountCards[this.paymentOptionIndex]?.nickname;
      else return this.accountCards[this.paymentOptionIndex]?.cardType;
  }

  getCardType() {
    if (!this.accountCards) return '';
    if (!this.accountCards[this.paymentOptionIndex]) return '';
    return this.accountCards[this.paymentOptionIndex]?.cardType;
  }

  async fetchData(fetchBill = false) {
    const loading = await this.loadingController.create({
      message: 'Loading Payment Options data...',
    });
    await loading.present();
    if (fetchBill) {
      //fetch bill to show update in payment info
      this.subs.sink = this.billService.fetchBill().subscribe();
    }
    this.subs.sink = this.settingsService.fetch().subscribe({
      next: async (data) => {
        await loading.dismiss();
      },
      error: async (e) => {
        await loading.dismiss();
        this.logger.error('unable to fetch user Payment Options data', e);
      },
    });
  }

  setToDefault(accountCardId: number) {
    const card = this.accountCards.find(
      (accountCard) => accountCard.accountPaymentId === accountCardId,
    );
    this.alertController
      .create({
        // cssClass: 'my-custom-class',
        header: 'Set this card as default card for payment?',
        // subHeader: 'Set this card to default?',
        message: card.cardType + ' ' + card.cardNumber,
        buttons: [
          {
            text: 'Cancel',
            role: 'cancel',
            // cssClass: 'secondary',
            handler: (blah) => {
              this.logger.log('Confirm Cancel: blah');
            },
          },
          {
            text: 'Set to default',
            cssClass: 'alert-accept',
            handler: () => {
              this.onSetDefault(card);
            },
          },
        ],
      })
      .then((alert) => {
        alert.present();
      });
  }

  onSetDefault(card: PaymentCard) {
    this.loadingController
      .create({
        message: 'Updating default card...',
      })
      .then((loader) => {
        loader.present();
        try {
          if (!environment.production) {
            this.logger.log('update payment cards');
          }
          this.saving = true;
          this.paymentCardService.updatePaymentCard(card).subscribe(
            () => {
              this.fetchData();
              this.accountCards = this.accountCards.sort((a) =>
                a.accountPaymentId === card.accountPaymentId ? -1 : 1,
              );
              this.slider.updateDefaultPaymentMethodId(card.accountPaymentId);
              this.changedPaymentMethod({
                index: this.paymentOptionIndex,
                methodId: this.accountCards[this.paymentOptionIndex]?.accountPaymentId,
              });
              this.toastController
                .create({
                  message: 'Default payment method updated successfully.',
                  color: 'success',
                  duration: 3000,
                })
                .then((toast) => {
                  toast.present();
                  this.slider.resetSlider(card);
                });
            },
            () => {
              this.toastController
                .create({
                  message: 'Error - Could not set default payment method.',
                  color: 'danger',
                  duration: 3000,
                })
                .then((toast) => {
                  toast.present();
                });
            },
            () => {
              loader.dismiss();
            },
          );
        } catch (err) {
          if (!environment.production) {
            console.error(err);
          }
          // send error log to server
          this.logger.error('onSetDefault', err);
          loader.dismiss();
          // display error toast
          showError('Error occurred while saving settings data!!', true);
        }
      })
      .finally(() => {
        this.saving = false;
      });
  }

  async updateNickname(event: any) {
    const card = this.accountCards[this.paymentOptionIndex];
    this.logger.log('OLD NICK-NAME (', 0, '):', card?.nickname);

    if (!card) {
      this.logger.error('Updating card nickname without available card?');
      return;
    }

    if (card?.nickname === this.newNickname) {
      return;
    }

    this.saving = true;

    this.logger.log('new nickname:', this.newNickname);
    this.logger.log('Changing nickname to:', this.newNickname);
    await this.paymentCardService
      .updateCardNickname(card?.accountPaymentId, this.newNickname)
      .toPromise();

    this.fetchData().then(() => {
      this.slider.resetSlider(card);
    });

    // this.accountCards$.subscribe({
    //   next: (accountCards) => {
    //     this.accountCards = accountCards.sort((a) => a.);
    //   },
    //   error: (err) => {
    //     this.logger.error('unable to retrieve user setting from data store', err);
    //   },
    // });

    this.saving = false;
    this.logger.log('NEW NICK-NAME (', 0, '):', card?.nickname);
  }

  changedPaymentMethod(paymentMethod: { index: number; methodId: number }) {
    this.paymentOptionIndex = paymentMethod.index;
    if (this.accountCards[paymentMethod.index]) {
      this.selectedAccountPaymentId = paymentMethod.methodId;
      this.updateLinkedBills();
      this.newNickname = this.accountCards[this.paymentOptionIndex].nickname;
    }
  }

  doRefresh(event: any) {
    this.fetchData().finally(() => {
      event.target.complete();
    });
  }

  onBillSwapped(billPaymentId: number) {
    this.loadingController
      .create({
        mode: 'md',
        message: 'Swapping bill...',
      })
      .then((loader) => {
        loader.present();
        this.billService.fetchBill(true).subscribe(() => {
          loader.dismiss();
        });
      });
  }

  onBillUnlinked(billPaymentId: number) {
    this.bills = this.bills.filter((bill) => bill.billPaymentId !== billPaymentId);
    this.linkedBills = this.linkedBills.filter((bill) => bill.billPaymentId !== billPaymentId);
    if (billPaymentId !== null && billPaymentId !== undefined) {
      this.loadingController
        .create({
          mode: 'md',
          message: 'Unlinking bill...',
        })
        .then((loader) => {
          loader.present();
          this.billService.fetchBill(true).subscribe(() => {
            loader.dismiss();
          });
        });
    }
  }

  ngOnDestroy() {
    this.subs.unsubscribe();
  }
}
